import { Formik } from 'formik'
import React, { useMemo } from 'react'

import * as Yup from 'yup'
import Input from '../../../../components/Form/Input'
import { Form, FormBody, FormFooter } from '../../../../components/Form/styles'
import * as S from '../../styles'
import cardValidator from 'card-validator'
import { Button } from '../../../../components'
import useActions from '../../../../hooks/useActions'
import { signUpActions } from '../../../../redux/ducks/signUp'
import { useSelector } from 'react-redux'
import ErrorMessage from '../../../../components/Form/ErrorMessage'
import Select from '../../../../components/Form/Select'
import { PaymentMethod } from '../../../../constants/order'
import LocalStorageService from '../../../../services/LocalStorageService'
import { SuccessMessage } from '../../../../components/Form/SuccessMessage'
import { centsToReal } from '../../../../helpers/centsToReal'
import { formatToReal } from '../../../../helpers/formatToReal'
import { useValidateCoupon } from '../helpers'

const CreditCardForm = () => {
  const user = LocalStorageService.get('user') || {}
  const { setStep4, nextStep, prevStep, pay, payWithExistingUser, setError } =
    useActions(signUpActions)
  const { step3, step4, error } = useSelector((state) => state.signUp)
  const { productSelected } = step3
  const [discountPercentage, setDiscountPercentage] = React.useState(0)

  const productAmount = useMemo(
    () =>
      centsToReal(
        productSelected.amount - productSelected.amount * discountPercentage
      ),
    [productSelected.amount, discountPercentage]
  )

  const installmentsForSelect = useMemo(
    () =>
      [...Array(productSelected.maxInstallments).entries()].map((_, index) => ({
        value: String(index + 1),
        label: `${index + 1}x ${formatToReal(productAmount / (index + 1))}`
      })),
    [productAmount, productSelected.maxInstallments]
  )

  const initialValues = useMemo(() => {
    const { installments, ...rest } = step4.creditCard
    const selectedInstallment = installmentsForSelect.find(
      (installment) => Number(installment.value) === installments
    )
    return {
      installments: selectedInstallment || '',
      ...rest
    }
  }, [installmentsForSelect, step4.creditCard])

  const onSubmit = async (
    { installments, cardCvv, coupon, ...values },
    { setSubmitting }
  ) => {
    setSubmitting(true)

    await setStep4({
      ...step4,
      creditCard: {
        ...values,
        installments: Number(installments.value),
        cardCvv: cardCvv.replace('_', '')
      },
      coupon
    })

    let purchaseSuccess = false

    // If the user is not empty, it means that the user is already logged in and have an account. So, it means that the payment is to buy another certificate for another brand of the same user company.
    if (user?.id) {
      purchaseSuccess = await payWithExistingUser(
        PaymentMethod.CREDIT_CARD,
        user
      )
    } else {
      purchaseSuccess = await pay(PaymentMethod.CREDIT_CARD)
    }

    if (purchaseSuccess) {
      nextStep()
    }

    setSubmitting(false)
  }

  const handleValidateCoupon = useValidateCoupon(
    setDiscountPercentage,
    setError,
    discountPercentage
  )

  const formSchema = useMemo(
    () =>
      Yup.object().shape({
        cardHolderName: Yup.string()
          .required('Campo obrigatório')
          .matches(/^[a-zA-Z\s]+$/, 'Nome do titular inválido'),
        cardNumber: Yup.string()
          .required('Campo obrigatório')
          .test(
            'cardNumber',
            'Número do cartão inválido',
            (value) => cardValidator.number(value).isPotentiallyValid
          ),
        cardExpiringDate: Yup.string()
          .required('Campo obrigatório')
          .max(5, 'Data inválida. Exemplo: MM/YY')
          .matches(
            /^((0[1-9])|(1[0-2]))\/(\d{2})/,
            'Data inválida. Exemplo: MM/YY'
          ),
        cardCvv: Yup.string()
          .required('Campo obrigatório')
          .matches(/^\d{3,4}_?$/, 'CVV inválido'),
        installments: Yup.string()
          .transform((_, obj) => obj?.value ?? undefined)
          .required('Campo obrigatório'),
        coupon: Yup.string()
          .optional()
          .nullable()
          .test('validateCoupon', 'Cupom inválido', handleValidateCoupon)
      }),
    [handleValidateCoupon]
  )

  return (
    <S.InputContainer>
      <Formik
        initialValues={initialValues}
        validationSchema={formSchema}
        onSubmit={onSubmit}
        validateOnChange={false}
      >
        {({ isSubmitting, getFieldProps }) => (
          <Form>
            <FormBody>
              <Input
                name="coupon"
                label="Cupom de desconto"
                showValidIcon={getFieldProps('coupon').value !== ''}
                display="inline-block"
              />
              <Input
                name="cardHolderName"
                label="Nome do titular"
                showValidIcon
                textTransform="uppercase"
              />
              <Input
                name="cardNumber"
                label="Número do cartão"
                showValidIcon
                mask="9999 9999 9999 9999"
              />
              <S.InputContainer>
                <Input
                  name="cardExpiringDate"
                  label="Data de expiração"
                  showValidIcon
                  display="inline-block"
                  mask="99/99"
                />
                <Input
                  name="cardCvv"
                  label="CVV"
                  showValidIcon
                  display="inline-block"
                  mask="9999"
                />
              </S.InputContainer>
              <Select
                name="installments"
                label="Número de parcelas"
                options={installmentsForSelect}
                optionValue="value"
                optionLabel="label"
                isClearable
              />
              {discountPercentage > 0 && (
                <SuccessMessage>
                  {`Cupom de desconto aplicado: ${discountPercentage * 100}%`}
                </SuccessMessage>
              )}
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FormBody>
            <FormFooter>
              <Button
                text="VOLTAR"
                type="button"
                variant="outlined"
                width="200px"
                onClick={prevStep}
              />
              <Button
                text="FINALIZAR"
                type="submit"
                variant="primary"
                width="200px"
                disabled={isSubmitting}
              />
            </FormFooter>
          </Form>
        )}
      </Formik>
    </S.InputContainer>
  )
}

export default CreditCardForm
